// Copies `text` to the clipboard in a way that works in as many
// environments as possible. Tries the modern Clipboard API first (only
// available in secure contexts: HTTPS or localhost) and falls back to
// the legacy `document.execCommand('copy')` via a hidden textarea.
//
// Returns a Promise<{ ok, mode, reason? }>:
//   - { ok: true,  mode: 'success'  } — modern API wrote to the clipboard.
//   - { ok: true,  mode: 'fallback' } — insecure context; execCommand
//                                       returned true but the result is
//                                       not verifiable. Treat as "may
//                                       or may not have worked" and let
//                                       the user confirm.
//   - { ok: false, mode: 'failed', reason } — neither path produced a
//                                       reliable copy.
//
// The distinction between 'fallback' and 'success' matters: in
// `fallback` we cannot trust the browser, so the UI must NOT show a
// "Copied" indicator that implies success — it must show a hint and
// ideally pre-select the text so Ctrl+C still works.
const legacyCopy = (text) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (_) {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
};

const copyToClipboard = async (text) => {
  if (text == null) return { ok: false, mode: 'failed', reason: 'empty' };
  const value = String(text);
  if (!value) return { ok: false, mode: 'failed', reason: 'empty' };

  // Modern API. Available only in secure contexts (HTTPS or localhost).
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return { ok: true, mode: 'success' };
    } catch (_) {
      // Permission denied or some other failure — fall through to legacy.
    }
  }

  // Insecure context. The legacy path may or may not actually write to
  // the clipboard (browsers have been known to return `true` silently
  // while dropping the data), so we surface it as a non-success state
  // and let the UI ask the user to verify.
  const execOk = legacyCopy(value);
  if (execOk) return { ok: true, mode: 'fallback', reason: 'insecure-context' };
  return { ok: false, mode: 'failed', reason: 'exec-failed' };
};

export default copyToClipboard;
